import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { LenderCrossPoolMeta, UserData } from '../utils/types'
import {
  createEulerMultiAccountTypeUserState,
  EulerUserReserveResponse,
} from '../utils/createEulerMultiAccountTypeUserState'
import {
  resolveSubAccountIndexes,
  getResolvedEulerSubAccountIndexes,
} from './userCallBuild'

/**
 * Returns a [converter, expectedCallCount] tuple for Euler V2 user data.
 *
 * Each raw result corresponds to one sub-account's `getAccountEnabledVaultsInfo` call.
 * The converter parses vault positions per sub-account and aggregates them
 * via `createEulerMultiAccountTypeUserState`.
 *
 * Sub-accounts are keyed by their numeric index (e.g. "0", "1", "34"),
 * NOT by derived hex addresses.
 *
 * @param subAccountIndexes - Numeric sub-account indexes that were fetched.
 *   Must match the indexes used in `buildEulerUserCall`.
 *   When undefined, reads from the build-phase resolved-indexes store
 *   (populated by `buildEulerUserCall`), falling back to [0].
 */
export const getEulerUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
  subAccountIndexes?: number[],
): [(data: any[]) => UserData | undefined, number] => {
  // Use explicit indexes → build-phase store → fallback [0]
  const effectiveIndexes = subAccountIndexes ??
    getResolvedEulerSubAccountIndexes(chainId, account) ?? [0]
  const sortedIndices = resolveSubAccountIndexes(account, effectiveIndexes)
  const expectedNumberOfCalls = sortedIndices.length

  return [
    (_data: any[]) => {
      if (_data.length !== expectedNumberOfCalls) {
        return undefined
      }

      const lendingPositions: EulerUserReserveResponse['lendingPositions'] = {}
      const controllers: EulerUserReserveResponse['controllers'] = {}
      const histData: {
        [subAccountIndex: string]: {
          totalDebt24h: number
          totalDeposits24h: number
        }
      } = {}

      for (let idx = 0; idx < sortedIndices.length; idx++) {
        const result = _data[idx]
        // Skip failed or empty results — the failure sentinel is a non-empty
        // string, so a plain truthiness check would let it through.
        if (isFailedCall(result) || !result) continue

        const evcInfo = result.evcAccountInfo ?? result[0]
        const vaultAccounts: any[] = result.vaultAccountInfo ?? result[1] ?? []

        // Use the numeric index as the sub-account identifier
        const subAccountId = String(sortedIndices[idx])

        // Extract controller vault (determines the config key / "mode").
        // Sourced from EVC account info, independent of vault positions — so a
        // sub-account with an enabled controller but no (or only zero-balance)
        // positions still surfaces its mode. Leverage-open relies on this to
        // emit a matching disableController item; without it the EVC batch
        // would leave a stale/duplicate controller and revert.
        const enabledControllers: string[] = (
          evcInfo?.enabledControllers ?? []
        ).map((a: string) => a.toLowerCase())
        const controller =
          enabledControllers.length > 0 ? enabledControllers[0] : undefined
        if (controller) {
          controllers[subAccountId] = controller
        }

        const positions: EulerUserReserveResponse['lendingPositions'][string] =
          {}
        let totalDebt24h = 0
        let totalDeposits24h = 0

        for (const va of vaultAccounts) {
          const vaultAddr = (va.vault?.toString() ?? '').toLowerCase()
          const assetAddr = (va.asset?.toString() ?? '').toLowerCase()
          const assets: bigint = BigInt(va.assets ?? 0)
          const borrowed: bigint = BigInt(va.borrowed ?? 0)

          // Skip positions with zero balance
          if (assets === 0n && borrowed === 0n) continue

          const marketUid = createMarketUid(chainId, lender, vaultAddr)
          const meta = metaMap?.[marketUid]
          const decimals = meta?.asset?.decimals ?? 18

          const depositsStr = parseRawAmount(assets.toString(), decimals)
          const debtStr = parseRawAmount(borrowed.toString(), decimals)
          const depositsNum = Number(depositsStr)
          const debtNum = Number(debtStr)

          // Prices
          const displayPrice = meta ? getDisplayPrice(meta) : 0
          const oraclePrice = meta ? getOraclePrice(meta) : 0
          const priceHist = meta?.price?.priceUsd24h ?? displayPrice

          const depositsUSD = depositsNum * displayPrice
          const debtUSD = debtNum * displayPrice
          const depositsUSDOracle = depositsNum * oraclePrice
          const debtUSDOracle = debtNum * oraclePrice

          totalDeposits24h += depositsNum * priceHist
          totalDebt24h += debtNum * priceHist

          positions[marketUid] = {
            marketUid,
            underlying: assetAddr,
            deposits: depositsStr,
            debt: debtStr,
            debtStable: '0',
            depositsUSD,
            debtUSD,
            debtStableUSD: 0,
            depositsUSDOracle,
            debtUSDOracle,
            debtStableUSDOracle: 0,
            collateralEnabled: Boolean(va.isCollateral),
            isAllowed: assets > 0n || borrowed > 0n,
          }
        }

        // Include sub-accounts with actual positions OR an active controller.
        // A controller-only (empty) sub-account yields an empty position map;
        // downstream aggregation still emits a record carrying
        // userConfig.selectedMode = controller so callers can detect and
        // disable it on a fresh leverage open.
        if (Object.keys(positions).length > 0 || controller) {
          lendingPositions[subAccountId] = positions
          histData[subAccountId] = { totalDebt24h, totalDeposits24h }
        }
      }

      // No positions found across any sub-account
      if (Object.keys(lendingPositions).length === 0) {
        return undefined
      }

      const payload: EulerUserReserveResponse = {
        chainId,
        lendingPositions,
        controllers,
        account,
      }

      return createEulerMultiAccountTypeUserState(payload, metaMap!, histData)
    },
    expectedNumberOfCalls,
  ]
}
